import React from 'react';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  titleSuffix?: React.ReactNode;
  children: React.ReactNode;
  hideTitle?: boolean;
  hideCloseButton?: boolean;
}

/**
 * Generic bottom sheet component that slides up from the bottom
 * Used for mobile drawers like CommitHistory, ProjectSettings, etc.
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  titleSuffix,
  children,
  hideTitle = false,
  hideCloseButton = false,
}: BottomSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[85dvh] p-0 flex flex-col rounded-t-3xl border-0"
        hideCloseButton
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header with title and close button */}
        {!hideTitle && title && (
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">{title}</h2>
              {titleSuffix}
            </div>
            {!hideCloseButton && (
              <button
                onClick={() => onOpenChange(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className={cn(
          "flex-1 overflow-hidden",
          !hideTitle && title ? "" : "pt-2"
        )}>
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
